/** @format */

import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ShieldCheck } from "lucide-react";
import { fitGuarantee, fitGuaranteeLinks } from "../lib/fitGuarantee";

// Fit Guarantee placements. Copy and links come from lib/fitGuarantee.js.
// Kept deliberately quiet: neutral palette, thin line icon, no badges.

const linkClass =
	"inline-flex items-center border-b border-current pb-px text-[#141414] hover:text-[#5E5E5A] transition-colors";

// Homepage value proposition, rendered just below the hero value strip
export const FitGuaranteeValueProp = () => {
	const { title, text, linkText } = fitGuarantee.home;
	return (
		<div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 py-8 sm:py-10 border-b border-[#E4E2DC]">
			<ShieldCheck
				aria-hidden="true"
				strokeWidth={1.25}
				className="w-7 h-7 text-[#5E5E5A] flex-shrink-0"
			/>
			<div className="max-w-3xl">
				<h2 className="font-instrument text-3xl sm:text-[34px] font-normal leading-tight tracking-[-0.01em]">
					{title}
				</h2>
				<p className="text-[15px] leading-relaxed text-[#5E5E5A] mt-2">{text}</p>
				<Link
					to={fitGuaranteeLinks.details}
					className={`${linkClass} mt-4 uppercase tracking-[0.2em] text-xs font-medium`}
				>
					{linkText}
				</Link>
			</div>
		</div>
	);
};

// Product builder: small expandable note, closed by default
export const FitGuaranteeInfo = ({ defaultOpen = false }) => {
	const [open, setOpen] = useState(defaultOpen);
	const panelId = useId();
	const { title, text, links } = fitGuarantee.builder;
	return (
		<div className="rounded-lg border border-gray-200 bg-white text-sm">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
				aria-controls={panelId}
				className="w-full flex items-center gap-2.5 px-4 py-3 text-left"
			>
				<ShieldCheck
					aria-hidden="true"
					strokeWidth={1.5}
					className="w-4 h-4 text-[#5E5E5A] flex-shrink-0"
				/>
				<span className="font-medium text-[#141414]">{title}</span>
				<ChevronDown
					aria-hidden="true"
					strokeWidth={1.5}
					className={`w-4 h-4 ml-auto text-[#5E5E5A] transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</button>
			{open && (
				<div id={panelId} className="px-4 pb-4 sm:pl-[42px] -mt-1">
					<p className="text-[#5E5E5A] leading-relaxed">{text}</p>
					<p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
						{links.map((link, i) => (
							<React.Fragment key={link.to}>
								{i > 0 && (
									<span aria-hidden="true" className="hidden sm:inline text-gray-300">
										|
									</span>
								)}
								<Link to={link.to} className={linkClass}>
									{link.text}
								</Link>
							</React.Fragment>
						))}
					</p>
				</div>
			)}
		</div>
	);
};

// Cart and checkout, next to the order summary
export const FitGuaranteeNote = () => {
	const { title, text, linkText } = fitGuarantee.cart;
	return (
		<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-5 flex items-start gap-3 text-sm">
			<ShieldCheck
				aria-hidden="true"
				strokeWidth={1.5}
				className="w-5 h-5 mt-0.5 text-[#5E5E5A] flex-shrink-0"
			/>
			<div>
				<p className="font-medium text-[#141414]">{title}</p>
				<p className="text-[#5E5E5A] leading-relaxed mt-1">{text}</p>
				<Link to={fitGuaranteeLinks.details} className={`${linkClass} mt-2 text-xs`}>
					{linkText}
				</Link>
			</div>
		</div>
	);
};

// Measuring guide reassurance (shown near the start and again before the shop CTA)
export const FitGuaranteeReassurance = () => {
	const { title, text, reassurance, linkText } = fitGuarantee.measuring;
	return (
		<aside className="border-l-2 border-[#E4E2DC] pl-5 md:pl-6 py-1">
			<h2 className="text-lg md:text-xl font-semibold text-gray-900">{title}</h2>
			<p className="text-gray-600 mt-1 leading-relaxed">{text}</p>
			<p className="text-gray-600 mt-2 leading-relaxed">
				{reassurance}{" "}
				<Link to={fitGuaranteeLinks.details} className={`${linkClass} whitespace-nowrap`}>
					{linkText}
				</Link>
			</p>
		</aside>
	);
};
